import { ref, shallowRef } from 'vue';
import axios from 'axios';

export function useManualSelection() {
  const self = {
    awaitingSelection: ref(false),
    selectionData: ref(null),
    selectedItems: ref([]),
    editedItems: shallowRef(new Map()),
    newItems: ref([]),
    editingItem: ref(null),
    addingNewItem: ref(false),
    submittingSelection: ref(false),
  };

  self.initSelection = (data) => {
    self.selectionData.value = data;
    self.awaitingSelection.value = true;
    self.selectedItems.value = [];
    self.editedItems.value.clear();
    self.newItems.value = [];
    self.editingItem.value = null;
    self.addingNewItem.value = false;

    if (data.selection_mode === 'single' && data.items.length > 0) {
      self.selectedItems.value = [0];
    }
  };

  self.toggleItemSelection = (itemIndex) => {
    if (!self.selectionData.value) return;

    const isSingleMode = self.selectionData.value.selection_mode === 'single';

    if (isSingleMode) {
      self.selectedItems.value = [itemIndex];
    } else {
      const currentIndex = self.selectedItems.value.indexOf(itemIndex);
      if (currentIndex > -1) {
        self.selectedItems.value.splice(currentIndex, 1);
      } else {
        self.selectedItems.value.push(itemIndex);
      }
    }
  };

  self.isItemSelected = (itemIndex) => {
    return self.selectedItems.value.includes(itemIndex);
  };

  self.selectAllItems = () => {
    if (!self.selectionData.value) return;
    self.selectedItems.value = self.selectionData.value.items.map((_, index) => index);
  };

  self.clearAllSelections = () => {
    self.selectedItems.value = [];
  };

  self.startEditingItem = (index) => {
    self.editingItem.value = index;
    if (!self.editedItems.value.has(index)) {
      const originalItem = self.selectionData.value.items[index];
      self.editedItems.value.set(index, { ...originalItem });
    }
  };

  self.saveItemEdit = () => {
    self.editingItem.value = null;
  };

  self.cancelItemEdit = (index) => {
    if (typeof index === 'string' && index.startsWith('new_')) {
      const newIndex = parseInt(index.split('_')[1]);
      self.newItems.value.splice(newIndex, 1);
    } else {
      self.editedItems.value.delete(index);
    }
    self.editingItem.value = null;
    self.addingNewItem.value = false;
  };

  self.startAddingNewItem = () => {
    const template = {};
    if (self.selectionData.value.display_fields && self.selectionData.value.display_fields.length > 0) {
      self.selectionData.value.display_fields.forEach(field => {
        template[field] = '';
      });
    } else if (self.selectionData.value.items.length > 0) {
      Object.keys(self.selectionData.value.items[0]).forEach(key => {
        template[key] = '';
      });
    }

    self.newItems.value.push({ ...template });
    self.editingItem.value = `new_${self.newItems.value.length - 1}`;
    self.addingNewItem.value = true;
  };

  self.removeNewItem = (newIndex) => {
    self.newItems.value.splice(newIndex, 1);
    if (self.editingItem.value === `new_${newIndex}`) {
      self.editingItem.value = null;
    }
    self.addingNewItem.value = false;
  };

  self.getItemData = (index) => {
    if (self.editedItems.value.has(index)) {
      return self.editedItems.value.get(index);
    }
    return self.selectionData.value.items[index];
  };

  self.updateItemField = (index, field, value) => {
    if (typeof index === 'string' && index.startsWith('new_')) {
      const newIndex = parseInt(index.split('_')[1]);
      self.newItems.value[newIndex][field] = value;
    } else {
      if (!self.editedItems.value.has(index)) {
        const originalItem = self.selectionData.value.items[index];
        self.editedItems.value.set(index, { ...originalItem });
      }
      self.editedItems.value.get(index)[field] = value;
    }
  };

  self.submitSelection = async (notify) => {
    if (!self.selectionData.value || (self.selectedItems.value.length === 0 && self.newItems.value.length === 0)) {
      notify('Please select at least one item to continue', 'warning');
      return null;
    }

    self.submittingSelection.value = true;

    try {
      const selectedIndices = [...self.selectedItems.value];

      const selectedItemsData = selectedIndices.map(index => {
        if (self.editedItems.value.has(index)) {
          return self.editedItems.value.get(index);
        }
        return self.selectionData.value.items[index];
      });

      const allSelectedItems = [...selectedItemsData, ...self.newItems.value];

      const response = await axios.post('/externalContent/submitChainSelection', {
        executionId: self.selectionData.value.executionId,
        selectedItems: allSelectedItems,
        selectedIndices: selectedIndices
      });

      if (response.data && response.data.success) {
        const childExecutions = response.data.childExecutions || [];
        const resultIndex = self.selectionData.value?.resultIndex;

        self.awaitingSelection.value = false;
        self.selectionData.value = null;
        self.selectedItems.value = [];
        self.editedItems.value.clear();
        self.newItems.value = [];
        self.editingItem.value = null;

        notify(`Selection submitted successfully. Processing ${childExecutions.length} items.`, 'success');

        return { success: true, childExecutions, resultIndex };
      } else {
        throw new Error(response.data?.message || 'Failed to submit selection');
      }
    } catch (error) {
      console.error('Error submitting selection:', error);
      notify(`Failed to submit selection: ${error.message}`, 'error');
      return null;
    } finally {
      self.submittingSelection.value = false;
    }
  };

  self.cancelSelection = (notify) => {
    self.awaitingSelection.value = false;
    self.selectionData.value = null;
    self.selectedItems.value = [];
    self.editedItems.value.clear();
    self.newItems.value = [];
    self.editingItem.value = null;
    self.addingNewItem.value = false;

    if (notify) {
      notify('Selection cancelled', 'info');
    }
  };

  return self;
}
